import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../components/ui/pagination';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Loader2, AlertCircle, Send, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const REVIEWS_PER_PAGE = 5;

export default function Reviews() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState({
    rating: 5,
    comment: '',
  });

  const [replyData, setReplyData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        navigate('/search');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Fetch property
        const propRes = await fetch(`${API_URL}/api/properties/${id}`, {
          headers,
        });

        if (propRes.ok) {
          const propData = await propRes.json();
          setProperty(propData);
        }

        // Fetch reviews
        const revRes = await fetch(`${API_URL}/api/reviews/property/${id}`, {
          headers,
        });

        if (revRes.ok) {
          const revData = await revRes.json();
          setReviews(revData.reviews || []);
        }
      } catch (error) {
        // Keep console.error for production error tracking
        toast.error('Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please log in to submit a review');
      navigate('/login', { state: { from: `/reviews/${id}` } });
      return;
    }

    if (!formData.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          property_id: id,
          rating: formData.rating,
          comment: formData.comment,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }

      const newReview = await response.json();
      setReviews([newReview, ...reviews]);
      setFormData({ rating: 5, comment: '' });
      toast.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyChange = (reviewId, value) => {
    setReplyData(prev => ({
      ...prev,
      [reviewId]: value,
    }));
  };

  const handleSubmitReply = async (reviewId) => {
    if (!replyData[reviewId]?.trim()) {
      toast.error('Please write a reply');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}/reply`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          reply: replyData[reviewId],
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit reply');
      }

      // Update review with reply
      setReviews(reviews.map(rev =>
        rev.id === reviewId
          ? { ...rev, owner_reply: replyData[reviewId] }
          : rev
      ));

      setReplyData(prev => ({
        ...prev,
        [reviewId]: '',
      }));

      toast.success('Reply submitted!');
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error(error.message);
    }
  };

  const paginatedReviews = reviews.slice(
    (currentPage - 1) * REVIEWS_PER_PAGE,
    currentPage * REVIEWS_PER_PAGE
  );
  const totalPages = Math.ceil(reviews.length / REVIEWS_PER_PAGE);

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const renderStars = (rating, onClick = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <button
            key={i}
            type="button"
            onClick={() => onClick && onClick(i)}
            className={`transition-colors ${onClick ? 'cursor-pointer hover:opacity-70' : ''}`}
          >
            <Star
              className={`h-5 w-5 ${
                i <= rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <Skeleton className="h-12 w-48 mb-8" />
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-full mb-4" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-bold text-slate-900 mb-2">Property Not Found</h1>
            <p className="text-slate-600 mb-6">The property you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/search')}>
              Back to Search
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Reviews for {property.title}
            </h1>
            <p className="text-slate-600">Read what others think about this property</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Sidebar - Rating Summary */}
            <div className="md:col-span-1">
              <Card className="sticky top-24">
                <CardContent className="p-6 space-y-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-slate-900 mb-2">
                      {averageRating}
                    </div>
                    <div className="flex justify-center mb-2">
                      {renderStars(Math.round(averageRating))}
                    </div>
                    <p className="text-sm text-slate-600">
                      {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                    </p>
                  </div>

                  {/* Rating distribution */}
                  <div className="space-y-2 border-t border-slate-200 pt-6">
                    {[5, 4, 3, 2, 1].map(rating => {
                      const count = reviews.filter(r => r.rating === rating).length;
                      const percent = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                      return (
                        <div key={rating} className="flex items-center gap-2 text-sm">
                          <span className="w-12 text-right text-slate-600">{rating}★</span>
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-amber-400 transition-all"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <span className="w-8 text-right text-slate-600">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* Write Review Form */}
              {isAuthenticated && user?.id !== property.owner_id && !reviews.find(r => r.reviewer_id === user.id) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card>
                    <CardHeader>
                      <CardTitle>Write a Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSubmitReview} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Rating</Label>
                          <div className="flex gap-2">
                            {renderStars(formData.rating, (rating) =>
                              setFormData(prev => ({ ...prev, rating }))
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="comment">Your Review</Label>
                          <Textarea
                            id="comment"
                            value={formData.comment}
                            onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                            placeholder="Share your experience with this property..."
                            rows={4}
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={submitting}
                          className="w-full bg-accent hover:bg-accent/90"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Submit Review
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Reviews List */}
              <AnimatePresence>
                {paginatedReviews.length > 0 ? (
                  <div className="space-y-4">
                    {paginatedReviews.map((review, idx) => (
                      <motion.div
                        key={review.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <Card>
                          <CardContent className="p-6 space-y-4">
                            {/* Reviewer Info */}
                            <div className="flex items-start justify-between">
                              <div className="flex gap-3">
                                <Avatar>
                                  <AvatarImage src={review.reviewer_picture} alt={review.reviewer_name} />
                                  <AvatarFallback>
                                    {review.reviewer_name?.split(' ')[0]?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-slate-900">{review.reviewer_name}</h4>
                                  <p className="text-xs text-slate-500">
                                    {new Date(review.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {renderStars(review.rating)}
                              </div>
                            </div>

                            {/* Review Comment */}
                            <p className="text-slate-700">{review.comment}</p>

                            {/* Owner Reply */}
                            {review.owner_reply && (
                              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                <p className="text-xs font-semibold text-slate-600 mb-2">Owner's Reply</p>
                                <p className="text-slate-700 text-sm">{review.owner_reply}</p>
                              </div>
                            )}

                            {/* Reply Form for Owner */}
                            {isAuthenticated && user?.id === property.owner_id && !review.owner_reply && (
                              <div className="border-t border-slate-200 pt-4">
                                <div className="space-y-2">
                                  <label className="text-xs font-semibold text-slate-600">
                                    Reply to Review
                                  </label>
                                  <div className="flex gap-2">
                                    <Textarea
                                      value={replyData[review.id] || ''}
                                      onChange={(e) => handleReplyChange(review.id, e.target.value)}
                                      placeholder="Share your response..."
                                      rows={2}
                                      className="text-sm"
                                    />
                                    <Button
                                      type="button"
                                      onClick={() => handleSubmitReply(review.id)}
                                      size="sm"
                                      className="h-fit"
                                    >
                                      <Send className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <AlertCircle className="h-8 w-8 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">No reviews yet. Be the first to review!</p>
                    </CardContent>
                  </Card>
                )}
              </AnimatePresence>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination>
                    <PaginationPrevious
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationNext
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
